import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

/** Redirect that preserves ?query and #hash (drip/UTM links depend on it). */
function RedirectQ({ to }) {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { api } from "@/lib/api";
import { CountryProvider } from "@/lib/country";
import ProtectedRoute from "@/components/ProtectedRoute";
import GlobalCalendlyBadge from "@/components/GlobalCalendlyBadge";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import AppUpdateToast from "@/components/AppUpdateToast";
import { trackPageView } from "@/lib/analytics";
import { isAdminContext, stopClarityIfRecording, enforceHealthRouteClarityPolicy } from "@/lib/consent";

/* GA4 page_view emitter for our SPA.
 *
 * Inlined here (not its own component file) because we hit a webpack
 * resolution oddity that prevented a separate component from mounting
 * inside <BrowserRouter>. Mounting it inline guarantees the React tree
 * picks it up.
 *
 * Why this exists: gtag.js's auto-pageview fires BEFORE React mounts,
 * so document.title is the static index.html title and SPA route
 * changes don't re-fire. We disabled the auto pageview in index.html
 * (`send_page_view: false`) and emit explicit page_view events with
 * `page_path`, `page_location`, `page_title` ourselves.
 */
function GA4PageTracker() {
    const location = useLocation();
    const lastSentRef = useRef(null);
    useEffect(() => {
        const key = `${location.pathname}${location.search || ""}`;
        if (lastSentRef.current === key) return;
        // Privacy gate: if the caregiver navigated INTO /admin (or
        // they're signed in as admin), stop Clarity recording so
        // session replays don't capture admin PII or pollute the
        // user-behavior dataset. Idempotent; safe to call repeatedly.
        if (isAdminContext()) stopClarityIfRecording();
        // Health-data route gate: on any page where the caregiver can
        // type PHI-adjacent content (chat, behavior notes, wellness,
        // burnout, appointment prep, patient profile, my-story, family
        // share), stop Clarity if it's recording AND refuse to boot
        // until they navigate off the page. GA4 still fires — it only
        // sees the route path, no form values. See
        // HEALTH_DATA_ROUTES in /lib/consent.js.
        enforceHealthRouteClarityPolicy(location.pathname);
        // First load: fire immediately. A delayed first page_view is the
        // main source of GA4 sessions landing with an empty/(not set)
        // page_path — fast bounces leave before the timer fires. The
        // analytics.js title fallback keeps page_title meaningful.
        if (lastSentRef.current === null) {
            lastSentRef.current = key;
            trackPageView();
            // UTM capture — remember which shared link brought them here so
            // the eventual signup is attributed in the Link performance table.
            try {
                const qs = new URLSearchParams(window.location.search);
                const campaign = (qs.get("utm_campaign") || "").trim();
                if (campaign) {
                    localStorage.setItem("ml_utm_campaign", campaign.slice(0, 64));
                    api.post("/utm/visit", {
                        campaign,
                        source: qs.get("utm_source") || "",
                        medium: qs.get("utm_medium") || "",
                        path: window.location.pathname,
                    }).catch(() => {});
                }
            } catch { /* private mode */ }
            return;
        }
        // 500 ms gives SeoHead time to mutate document.title for the new
        // route. We use a longer delay than the previous 250ms because
        // Helmet's title update lags React commit by ~one tick on slow
        // devices — the analytics.js fallback already prevents a blank
        // page_title, this just maximizes the chance we capture the
        // route-specific title instead.
        const id = window.setTimeout(() => {
            lastSentRef.current = key;
            trackPageView();
        }, 500);
        return () => window.clearTimeout(id);
    }, [location.pathname, location.search]);
    return null;
}

import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import AboutPage from "@/pages/AboutPage";
import BetaSignup from "@/pages/BetaSignup";
import Login from "@/pages/Login";
import MagicLinkConsume from "@/pages/MagicLinkConsume";
import Unsubscribe from "@/pages/Unsubscribe";
import Ecosystem from "@/pages/Ecosystem";
import HomePreviewMockup from "@/pages/HomePreviewMockup";
import ResourcesHub from "@/pages/ResourcesHub";
import PartnerInquiry from "@/pages/PartnerInquiry";
import WaitlistPending from "@/pages/WaitlistPending";
// PrivateBetaWall removed — /wall has been deleted. Anyone unauthenticated
// is now sent to "/" (the landing page) which already has the signup form
// and the "Book a free 15-min NP session" CTA right at the top.
import Demo from "@/pages/Demo";
import AdminDashboard from "@/pages/AdminDashboard";
import Dashboard from "@/pages/Dashboard";
import Burnout from "@/pages/Burnout"; // legacy — Wellness now covers burnout; route aliased below
import Journey from "@/pages/Journey";
import Settings from "@/pages/Settings";
import SharedUpdateView from "@/pages/SharedUpdateView";
import OrgIntelligence from "@/pages/OrgIntelligence";
// Appointment Prep merged into CareTeamSummary — /appointment route now renders that page.
import Resources from "@/pages/Resources";
import Roadmap from "@/pages/Roadmap";
import LovedStory from "@/pages/LovedStory";
import Family from "@/pages/Family";
import Chat from "@/pages/Chat";
import Feedback from "@/pages/Feedback";
import Navigator from "@/pages/Navigator";
import CareNetwork from "@/pages/CareNetwork";
import WhoPays from "@/pages/WhoPays";
import Treatments from "@/pages/Treatments";
import Book from "@/pages/Book";
import BookingSuccess from "@/pages/BookingSuccess";
import StudyComplete from "@/pages/StudyComplete";
import Guidance from "@/pages/Guidance";
import Education from "@/pages/Education";
import Intake from "@/pages/Intake";
import YCReviewer from "@/pages/YCReviewer";
import Conversations from "@/pages/Conversations";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Security from "@/pages/Security";
import Cookies from "@/pages/Cookies";
import CareSummary from "@/pages/CareSummary";
import ConsentBanner from "@/components/ConsentBanner";
import Pricing from "@/pages/Pricing";
import Refer from "@/pages/Refer";
import Support from "@/pages/Support";
import JoinByInvite from "@/pages/JoinByInvite";
import TesterLogin from "@/pages/TesterLogin";
import Screener from "@/pages/Screener";
import Partners from "@/pages/Partners";
import PartnersHub from "@/pages/PartnersHub";
import CommunityPartners from "@/pages/CommunityPartners";
import CareShareView from "@/pages/CareShareView";
import CareHistory from "@/pages/CareHistory";
import Guide from "@/pages/Guide";
import GuideScreenersShare from "@/pages/GuideScreenersShare";
import MyStory from "@/pages/MyStory";
import Wellness from "@/pages/Wellness";
import SharedReport from "@/pages/SharedReport";
import CareTeam from "@/pages/CareTeam";
import ToolsHub, { JourneyQuiz, GuideCheck, MemoryCheck } from "@/pages/Tools";
import { WellnessCheckPublic } from "@/pages/Wellness";
import OrgAdmin from "@/pages/OrgAdmin";
import {
    LearnArticle, BehaviorArticle, GuideArticle, PayingForCareArticle,
    ResourceArticle, TreatmentArticle, HardConversationArticle,
} from "@/pages/ArticlePage";
import AuthorAshlee from "@/pages/AuthorAshlee";
import Library from "@/pages/Library";
import CaregiverTools from "@/pages/CaregiverTools";
import CaregiverToolPage from "@/pages/CaregiverToolPage";
import InvestorDemo from "@/pages/InvestorDemo";
import DemoOrganization from "@/pages/DemoOrganization";
import DemoGuide from "@/components/DemoGuide";

// Legacy share links (passport / patient-profile / care-team) were retired
// when everything consolidated into the Care Summary builder (Jun 2026).
// Recipients who click an old link get a calm explanation, not a 404.
function RetiredShareNotice() {
    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="retired-share-notice">
            <div className="ml-card p-8 max-w-md w-full text-center">
                <h1 className="text-xl font-heading font-medium text-[#1F2624]">This share link has been retired.</h1>
                <p className="mt-3 text-sm text-[#4C5551] leading-relaxed">
                    Memory Lane now shares everything through a single, caregiver-controlled
                    Care Summary link. Ask the caregiver to send you a fresh link — it takes
                    them under a minute to create.
                </p>
                <a href="/" className="inline-block mt-5 px-5 py-2.5 rounded-2xl text-sm font-medium bg-teal-dark text-white hover:bg-teal no-underline" data-testid="retired-share-home-link">
                    About Memory Lane
                </a>
            </div>
        </div>
    );
}

// Public-only Resources page wrapper (no auth required)
function PublicResources() {
    return (
        <div className="min-h-screen bg-cream">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <Resources />
            </div>
        </div>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <CountryProvider>
                    <BrowserRouter>
                    <GA4PageTracker />
                    <GlobalCalendlyBadge />
                    <InstallAppPrompt />
                    <ConsentBanner />
                    <DemoGuide />
                    <Routes>
                        {/* Public */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/shared-update/:token" element={<SharedUpdateView />} />
                        <Route path="/beta" element={<BetaSignup />} />
                        {/* Explicit redirects so external backlinks / bots /
                            old marketing material to /signup, /about, /contact
                            don't get silently caught by the catch-all and show
                            up in GA4 as transient page_views from a route that
                            doesn't exist. Using Navigate with replace=true so
                            history stays clean. */}
                        <Route path="/signup" element={<Navigate to="/beta" replace />} />
                        <Route path="/sign-up" element={<Navigate to="/beta" replace />} />
                        <Route path="/register" element={<Navigate to="/beta" replace />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<Navigate to="/support" replace />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/magic" element={<MagicLinkConsume />} />
                        <Route path="/unsubscribe" element={<Unsubscribe />} />
                        <Route path="/ecosystem" element={<Ecosystem />} />
                        <Route path="/mockups/home" element={<HomePreviewMockup />} />
                        <Route path="/partners/inquiry" element={<PartnerInquiry />} />
                        <Route path="/demo" element={<Demo />} />
                        {/* Public NP lead-gen page — the landing 'Talk to a dementia NP'
                            CTA targets unauthenticated visitors; Navigator has its own
                            header and posts to the public /navigator-request endpoint. */}
                        <Route path="/navigator" element={<Navigator />} />
                        {/* Free public tools — shareable, no login, funnel to /beta */}
                        <Route path="/tools" element={<ToolsHub />} />
                        <Route path="/tools/journey-quiz" element={<JourneyQuiz />} />
                        <Route path="/tools/guide-check" element={<GuideCheck />} />
                        <Route path="/tools/memory-check" element={<MemoryCheck />} />
                        <Route path="/wellness-check" element={<WellnessCheckPublic />} />
                        <Route path="/resources" element={<PublicResources />} />
                        <Route path="/library" element={<Library />} />
                        <Route path="/resources/community-partners" element={<CommunityPartners />} />
                        <Route path="/passport/share/:token" element={<RetiredShareNotice />} />
                        <Route path="/patient-profile/share/:token" element={<RetiredShareNotice />} />
                        <Route path="/s/:token" element={<CareShareView />} />
                        <Route path="/yc" element={<YCReviewer />} />
                        <Route path="/study-complete" element={<StudyComplete />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/security" element={<Security />} />
                        <Route path="/cookies" element={<Cookies />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/refer" element={<Refer />} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/join/:code" element={<JoinByInvite />} />
                        <Route path="/tester/:code" element={<TesterLogin />} />
                        <Route path="/tester" element={<TesterLogin />} />
                        <Route path="/tester/" element={<TesterLogin />} />

                        {/* Legacy Care Team Summary share links — retired */}
                        <Route path="/share/care-team/:token" element={<RetiredShareNotice />} />
                        <Route path="/share/guide-screeners/:token" element={<GuideScreenersShare />} />

                        {/* SEO standalone articles — one URL per topic.
                            Routes are intentionally PUBLIC (no auth gate) so
                            Google can crawl, and each Article is rendered by
                            ArticlePage with its own SeoHead + JSON-LD. */}
                        <Route path="/learn/:slug" element={<LearnArticle />} />
                        <Route path="/behavior/:slug" element={<BehaviorArticle />} />
                        <Route path="/guide/:slug" element={<GuideArticle />} />
                        <Route path="/paying-for-care/:slug" element={<PayingForCareArticle />} />
                        <Route path="/resources/:slug" element={<ResourceArticle />} />
                        <Route path="/treatments/:slug" element={<TreatmentArticle />} />
                        <Route path="/hard-conversations/:slug" element={<HardConversationArticle />} />

                        {/* Author page — Person + ProfilePage schema for E-E-A-T.
                            Referenced by every Article's `author.url`. */}
                        <Route path="/authors/ashlee-skabla-velez" element={<AuthorAshlee />} />
                        <Route path="/caregiver-tools" element={<CaregiverTools />} />
                        <Route path="/caregiver-tools/:slug" element={<CaregiverToolPage />} />
                        <Route path="/investor-demo" element={<InvestorDemo />} />
                        <Route path="/investor-demo/organization" element={<DemoOrganization />} />

                        {/* Organizational partner entry — pilots, org sign-in, invite code redemption */}
                        <Route path="/partners" element={<PartnersHub />} />
                        <Route path="/organizations" element={<Partners />} />
                        <Route path="/for-partners" element={<Partners />} />

                        {/* Public shared caregiver-update snapshot (no auth, expires in 7 days) */}
                        <Route path="/r/:token" element={<SharedReport />} />

                        {/* Waitlist user landing */}
                        <Route path="/pending" element={
                            <ProtectedRoute allowWaitlist>
                                <WaitlistPending />
                            </ProtectedRoute>
                        } />

                        {/* Admin */}
                        <Route path="/admin" element={
                            <ProtectedRoute role="admin">
                                <AdminDashboard />
                            </ProtectedRoute>
                        } />

                        {/* Intake (no Layout — focused flow) */}
                        <Route path="/intake" element={
                            <ProtectedRoute>
                                <Intake />
                            </ProtectedRoute>
                        } />

                        {/* Pre-screener gate for testers (no Layout) */}
                        <Route path="/screener" element={
                            <ProtectedRoute>
                                <Screener />
                            </ProtectedRoute>
                        } />

                        {/* Approved app routes */}
                        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/care-summary" element={<CareSummary />} />
                            <Route path="/burnout" element={<Wellness />} />
                            {/* Our Journey hub — behaviors, meds, appointments, documents, about */}
                            <Route path="/journey" element={<Journey />} />
                            <Route path="/journey/:section" element={<Journey />} />
                            <Route path="/settings" element={<Settings />} />
                            {/* Rehomed routes → redirect into Our Journey (query preserved for drip links) */}
                            <Route path="/behavior" element={<RedirectQ to="/journey/behaviors" />} />
                            <Route path="/patient" element={<RedirectQ to="/journey/about" />} />
                            <Route path="/documents" element={<RedirectQ to="/journey/documents" />} />
                            <Route path="/appointment" element={<RedirectQ to="/journey/appointments" />} />
                            <Route path="/care-team-summary" element={<RedirectQ to="/journey/appointments" />} />
                            <Route path="/guidance" element={<Guidance />} />
                            <Route path="/conversations" element={<Conversations />} />
                            <Route path="/roadmap" element={<Roadmap />} />
                            <Route path="/roadmap/story" element={<LovedStory />} />
                            <Route path="/care-team" element={<CareTeam />} />
                            <Route path="/family" element={<Family />} />
                            <Route path="/chat" element={<Chat />} />
                            <Route path="/education" element={<Education />} />
                            <Route path="/feedback" element={<Feedback />} />
                            <Route path="/help-finder" element={<Resources />} />
                            <Route path="/resources-hub" element={<ResourcesHub />} />
                            <Route path="/who-pays" element={<WhoPays />} />
                            <Route path="/treatments" element={<Treatments />} />
                            <Route path="/care-network" element={<CareNetwork />} />
                            <Route path="/book" element={<Book />} />
                            <Route path="/book/success" element={<BookingSuccess />} />
                            <Route path="/my-story" element={<MyStory />} />
                            <Route path="/wellness" element={<Wellness />} />
                            <Route path="/share-with-care-team" element={<Navigate to="/care-summary" replace />} />
                            <Route path="/org-admin" element={<OrgAdmin />} />
                            <Route path="/org-intel" element={<OrgIntelligence />} />
                            <Route path="/care-history" element={<CareHistory />} />
                            <Route path="/guide" element={<Guide />} />
                            <Route path="/passport" element={<Navigate to="/care-summary" replace />} />
                            <Route path="/continuity-passport" element={<Navigate to="/care-summary" replace />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                </CountryProvider>
            </AuthProvider>
            <Toaster position="top-center" richColors />
            <AppUpdateToast />
        </div>
    );
}

export default App;
